/** 解析限時商品 playConfig JSON */
export function parseTimedPlayConfig(playConfig) {
  if (!playConfig) {
    return { durationSeconds: 0, payoutOdds: 0 };
  }
  try {
    const json = typeof playConfig === 'string' ? JSON.parse(playConfig) : playConfig;
    return {
      durationSeconds: Number(json.durationSeconds) || 0,
      payoutOdds: Number(json.payoutOdds) || 0,
    };
  } catch {
    return { durationSeconds: 0, payoutOdds: 0 };
  }
}

/** 依 sort 取最小者（後端已排序，首筆即最小） */
export function pickPrimaryTimedProduct(products) {
  if (!Array.isArray(products) || products.length === 0) {
    return null;
  }
  return [...products].sort((a, b) => (a.sort ?? 9999) - (b.sort ?? 9999))[0];
}

/** 依交易對選擇限時商品（同標的可能有多個持倉秒數） */
export function pickTimedProductBySymbol(products, marketSymbolCode) {
  if (!marketSymbolCode) {
    return pickPrimaryTimedProduct(products);
  }
  const normalized = String(marketSymbolCode).trim().toUpperCase();
  const matched = products.filter(
    (item) => String(item.marketSymbolCode || '').trim().toUpperCase() === normalized
  );
  if (matched.length === 0) {
    return pickPrimaryTimedProduct(products);
  }
  return [...matched].sort((a, b) => (a.sort ?? 9999) - (b.sort ?? 9999))[0];
}

/** 解析後端 yyyy-MM-dd HH:mm:ss 時間字串 */
export function parseDateTime(value) {
  if (!value) {
    return null;
  }
  const ms = new Date(String(value).replace(' ', 'T')).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * 計算限時訂單倒數進度（0～100）。
 * 依 createTime + durationSeconds 或 expireTime 推算。
 */
export function calcTimedOrderProgress(order, nowMs = Date.now()) {
  const durationSeconds = Number(order?.durationSeconds) || 0;
  const expireMs = parseDateTime(order?.expireTime);
  let startMs = parseDateTime(order?.createTime);
  let endMs = expireMs;

  if (!endMs && startMs && durationSeconds > 0) {
    endMs = startMs + durationSeconds * 1000;
  }
  if (!startMs && endMs && durationSeconds > 0) {
    startMs = endMs - durationSeconds * 1000;
  }
  if (!startMs || !endMs || endMs <= startMs) {
    return { percent: 0, remainingSeconds: 0, completed: true };
  }

  const total = endMs - startMs;
  const elapsed = Math.max(0, Math.min(total, nowMs - startMs));
  const percent = Math.min(100, (elapsed / total) * 100);
  const remainingSeconds = Math.max(0, Math.ceil((endMs - nowMs) / 1000));

  return {
    percent,
    remainingSeconds,
    completed: nowMs >= endMs,
  };
}

export function isTimedOrderPending(order) {
  return order?.status === 'PROCESS' || order?.status === 'START';
}

/** 同標的下的結算時間選項（不同 productId / playConfig） */
export function listDurationOptions(products, marketSymbolCode) {
  if (!marketSymbolCode) {
    return [];
  }
  return products
    .filter((item) => item.marketSymbolCode === marketSymbolCode)
    .map((item) => {
      const cfg = parseTimedPlayConfig(item.playConfig);
      return {
        productId: item.id,
        durationSeconds: cfg.durationSeconds,
        payoutOdds: cfg.payoutOdds,
        payoutPercent: Math.round(cfg.payoutOdds * 100),
        product: item,
      };
    })
    .sort((a, b) => a.durationSeconds - b.durationSeconds);
}

export function formatPrice(value, digits = 8) {
  if (value == null || value === '') {
    return '—';
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return String(value);
  }
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: digits,
  });
}

export function calcOrderPreview(amount, handlingFeeRate, payoutOdds) {
  const bet = Number(amount);
  if (!bet || bet <= 0) {
    return { fee: 0, margin: 0, expectedProfit: 0 };
  }
  const rate = Number(handlingFeeRate) || 0;
  const odds = Number(payoutOdds) || 0;
  const fee = bet * rate;
  const margin = bet - fee;
  const expectedProfit = bet * odds;
  return { fee, margin, expectedProfit };
}
